import {createContext, useContext, useEffect, useRef, useState} from 'react';
import {bool} from 'prop-types';
import ImportSvg from 'imagesUwKluis/svg/import.svg';
import Stack from '../../shared/layouts/Stack';
import Card from '../../shared/Card';
import Header from '../../shared/Header';
import Button from '../../shared/Button';
import CompareApplicant from './applicant/CompareApplicantPage';
import ComparePartner from './partner/ComparePartnerPage';
import CompareResidence from './house/CompareResidencePage';
import CompareMortgage from './mortgage/CompareMortgagePage';
import CompareOrv from './orv/CompareOrvPage';
import useForm from '../../../hooks/useForm';
import axios from 'axios';
import {cloneDeep, get, isEmpty, set} from 'lodash';
import MessagebarContext from '../../../context/MessagebarContext';
import UserContext from '../../../context/UserContext';
import PreventUnload from '../../shared/PreventUnload';
import {checkAndFormatDates} from '../../../utils/dateHelpers';
import Loader from '../../shared/Loader';
import {Flex} from '../../shared/layouts';
import {useTranslation} from "react-i18next";

export const CompareContext = createContext();

const healthFields = ['is_smoker'];
const workIncomeFields = ['gross_yearly_income', 'pension_income'];
const contactInfoFields = [
  // 'zipcode',
  // 'housenumber',
  // 'housenumber_addition',
  // 'street',
  // 'place',
  'phone_number',
  'email',
];
const personFields = [
  'firstname',
  'initials',
  'addition',
  'lastname',
  'date_of_birth',
  'gender',
];
const addressFields = [
  'zipcode',
  'housenumber',
  'housenumber_addition',
  'street',
  'place',
];
const homeFields = [
  'type',
  'taxation_value_date',
  'taxation_value_amount',
  'woz_value_amount',
  'woz_value_date',
];
const mortgageFields = [
  'mortgagenumber',
  'originalAmount',
  'provider',
  'label',
];
const loanpartFields = [
  'currentAmount',
  'duration',
  'frtDuration',
  'frtStartDate',
  'loanpartnumber',
  'loanparttype',
  'originalAmount',
  'startDate',
  'nhg',
  'interest',
  'ltv',
];

const orvFields = [
  'insurance_company',
  'insurance_type',
  'insurance_subtype',
  'policy_number',
  'pawned',
  'premium.amount',
  'premium.period',
  'premium.duration_in_months',
  'amount',
  'period',
  'duration_in_months',
];

const orvCoverageFields = [
  'policy_holder',
  'coverage_type',
  'covered',
  'coverage',
  'start_date',
  'end_date',
];

const pages = {
  applicant: {
    applicant_contact: contactInfoFields,
    applicant_person: personFields,
    applicant_health: healthFields,
    applicant_income: workIncomeFields,
  },
  partner: {
    partner_contact: contactInfoFields,
    partner_person: personFields,
    partner_health: healthFields,
    partner_income: workIncomeFields,
  },
  mortgage: {
    mortgage_general: mortgageFields,
    mortgage_loanpart: loanpartFields,
  },
  residence: {
    residence_address: addressFields,
    residence_home: homeFields,
  },
  orv: {
    orv_general: orvFields,
    orv_coverage: orvCoverageFields,
  },
};

const ComparePage = ({ veiligOphalen }) => {
  // local state
  const [isLoading, setIsLoading] = useState(true);
  const [visibleSection, setVisibleSection] = useState(null);
  const [copiedFields, setCopiedFields] = useState([]);

  const [ownApplicantData, updateOwnApplicantData, setOwnApplicantData] =
    useForm({});
  const [ownPartnerData, updateOwnPartnerDataChange, setOwnPartnerData] =
    useForm({});
  const [ownAddressData, updateOwnAddressData, setOwnAddressData] = useForm({});
  const [ownHouseData, updateOwnHouseData, setOwnHouseData] = useForm({});
  const [ownMortgageData, , setOwnMortgageData] = useForm([]);
  const [ownInsuranceData, , setOwnInsuranceData] = useForm([]);

  const [meta, setMeta] = useState({});

  const [otherApplicantData, setOtherApplicantData] = useState({});
  const [otherPartnerData, setOtherPartnerData] = useState({});
  const [otherAddressData, setOtherAddressData] = useState({});
  const [otherHouseData, setOtherHouseData] = useState({});
  const [otherMortgageData, setOtherMortgageData] = useState([]);
  const [otherInsuranceData, setOtherInsuranceData] = useState([]);

  // External states
  const mountedRef = useRef(true);
  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const { setChangedDataWithoutSaving } = useContext(UserContext);
  const { t } = useTranslation();

  // Effects
  useEffect(() => {
    setChangedDataWithoutSaving(false);
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get('/dossier/data'),
      axios.get(
        veiligOphalen
          ? '/dossier/veiligophalen/dossier-data'
          : `/dossier/org/data`
      ),
    ])
      .then(
        ([
          { data: dossierData },
          { data: externalDossier },
        ]) => {
          if (!mountedRef.current) return null;

          // Internal
          setOwnApplicantData(checkAndFormatDates(dossierData.applicant));
          setOwnPartnerData(checkAndFormatDates(dossierData?.partner || {}));
          setOwnAddressData(checkAndFormatDates(dossierData?.address || {}));
          setOwnHouseData(checkAndFormatDates(dossierData?.house || {}));
          setOwnMortgageData(checkAndFormatDates(dossierData?.mortgage || []));
          setOwnInsuranceData(checkAndFormatDates(dossierData?.insurances || []));

          setMeta({
            insuranceProviders: dossierData?.meta?.insurance_providers || [],
            mortgageProviders: dossierData?.meta?.mortgage_providers || [],
            repaymentTypes: dossierData?.meta?.mortgage_repayment_types || [],
          });

          // External
          setOtherApplicantData(checkAndFormatDates(externalDossier.applicant));
          setOtherPartnerData(checkAndFormatDates(externalDossier?.partner || {}));
          setOtherAddressData(checkAndFormatDates(externalDossier?.address || {}));
          setOtherHouseData(checkAndFormatDates(externalDossier?.house || {}));
          setOtherMortgageData(checkAndFormatDates(externalDossier?.mortgage || []));
          setOtherInsuranceData(checkAndFormatDates(externalDossier?.insurances || []));
        }
      )
      .catch((err) => {
        setAlertMessage();
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, [veiligOphalen]);

  // Functions
  const isFieldCopied = (value) => copiedFields.some((val) => val === value);
  const hasSectionCopiedFields = (value) =>
    copiedFields.some((val) => val.includes(value));
  const hasPageCopiedFields = hasSectionCopiedFields;

  const hasDiff = (fields, ownData, compareData) => {
    const isOwnDataEmpty = isEmpty(ownData);
    const isCompareDataEmpty = isEmpty(compareData);

    if (isOwnDataEmpty || isCompareDataEmpty) {
      // If both are empty there is nothing to compare
      if (isOwnDataEmpty && isCompareDataEmpty) return false;
      // If there is no compare-data there nothing to compare
      if (isCompareDataEmpty) return false;
      // If there is compare-data, then check if contains useful data
      else {
        return fields.some((key) => get(compareData, key, null) != null);
      }
    } else {
      return fields.some((key) => {
        if (key.includes('.')) {
          return get(ownData, key, null) !== get(compareData, key, null);
        }
        if (ownData[key] == null && compareData[key] == null) {
          return false;
        }
        if (ownData[key]?.date || compareData[key]?.date) {
          return ownData[key]?.date !== compareData[key]?.date;
        }
        return ownData[key] !== compareData[key];
      });
    }
  };

  const shouldShow = (fields, ownData, compareData, section) => {
    let copyCheck = false;
    //if value is copied, keep showing the field
    if (Array.isArray(fields)) {
      copyCheck = fields.some((field) => isFieldCopied(`${section}-${field}`));
    } else {
      copyCheck = isFieldCopied(`${section}-${fields}`);
    }

    return (
      visibleSection !== null ||
      copyCheck ||
      hasDiff(Array.isArray(fields) ? fields : [fields], ownData, compareData)
    );
  };

  // Mutations
  const handleApplicantDataChange = (e, section) => {
    updateOwnApplicantData(e);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [...values, `${section}-${e.target.name}`]);
  };

  const handlePartnerDataChange = (e, section) => {
    updateOwnPartnerDataChange(e);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [...values, `${section}-${e.target.name}`]);
  };

  const handleAddressDataChange = (e, section) => {
    updateOwnAddressData(e);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [...values, `${section}-${e.target.name}`]);
  };

  const handleHouseDataChange = (e, section) => {
    updateOwnHouseData(e);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [...values, `${section}-${e.target.name}`]);
  };

  const handleMortgageDataChange = (
    { target: { name, value } },
    index,
    section,
    fieldName
  ) => {
    const mortgages = cloneDeep(ownMortgageData || []);

    if (!mortgages[index]) {
      mortgages[index] = {};
    }

    set(mortgages[index], name, value);

    setOwnMortgageData(mortgages);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [
      ...values,
      `${section}-${fieldName ? fieldName : name}`,
    ]);
  };

  const handleInsuranceDataChange = (
    { target: { name, value } },
    index,
    section,
    fieldName
  ) => {
    const insurances = cloneDeep(ownInsuranceData || []);

    if (!insurances[index]) {
      insurances[index] = {};
    }

    set(insurances[index], name, value);

    setOwnInsuranceData(insurances);
    setChangedDataWithoutSaving(true);
    setCopiedFields((values) => [
      ...values,
      `${section}-${fieldName ? fieldName : name}`,
    ]);
  };

  const customOwnAddressSetter = (newAddressData) => {
    setOwnAddressData({
      ...newAddressData,
      country: ownAddressData?.country ?? null,
      province: ownAddressData?.province ?? null,
    });
  };

  // Copy
  const handleCopyAllFields = (dataToCopy, existingData, page, cb, indexes = [], prefix) => {
    const pageWhitelist = pages[page];
    const dataIsArray = Array.isArray(dataToCopy);
    const defaultValue = dataIsArray ? [] : {};

    let copyValue = defaultValue;
    const toFields = existingData || defaultValue;

    if (dataIsArray) {
      copyValue = dataToCopy.map((obj, index) => handleCopyAllFields(obj, existingData?.[index], page, undefined, [...indexes, index]));
    } else if (dataToCopy instanceof Object) {
      copyValue = Object.entries(dataToCopy).reduce((total, [key, value]) => {
        const section = Object.keys(pageWhitelist).find(section => pageWhitelist[section].some(val => val === key))
        if (section) {
          total[key] = value;
          if (value !== toFields[key]) {
            setCopiedFields((values) => [
              ...values,
              `${section}${indexes.length > 0 ? '_' + indexes.join('') : ''}-${prefix || ''}${key}`,
            ]);
          }
        } else if (['loanparts', 'coverages'].includes(key)) {
          total[key] = handleCopyAllFields(value, existingData[key], page, undefined, indexes);
        } else if (key === 'premium') {
          total[key] = handleCopyAllFields(value, existingData[key], page, undefined, indexes, 'premium.');
        }

        return total;
      }, {})
    }

    if (cb) {
      cb(copyValue)
    } else {
      return copyValue;
    }
  };

  const handleCopyDossier = () => {
    handleCopyAllFields(otherApplicantData, ownApplicantData, 'applicant', setOwnApplicantData);
    handleCopyAllFields(otherPartnerData, ownPartnerData,'partner', setOwnPartnerData);
    handleCopyAllFields(otherAddressData, ownAddressData, 'residence', customOwnAddressSetter);
    handleCopyAllFields(otherHouseData, ownHouseData, 'residence', setOwnHouseData);
    handleCopyAllFields(otherMortgageData, ownMortgageData, 'mortgage', setOwnMortgageData);
    handleCopyAllFields(otherInsuranceData, ownInsuranceData, 'orv', setOwnInsuranceData);
  }

  // Submit
  const onSave = (e) => {
    e.preventDefault();
    setIsLoading(true);
    axios({
      method: 'post',
      url: '/dossier',
      data: {
        applicant: checkAndFormatDates(ownApplicantData, true),
        partner: checkAndFormatDates(ownPartnerData, true),
        address: checkAndFormatDates(ownAddressData, true),
        house: checkAndFormatDates(ownHouseData, true),
        mortgage: checkAndFormatDates(ownMortgageData, true),
        insurances: checkAndFormatDates(ownInsuranceData, true),
      },
    })
      .then(() => {
        setSuccesMessage();
        setChangedDataWithoutSaving(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setAlertMessage();
        console.log(err);
        setIsLoading(false);
      });
  };

  return (
    <CompareContext.Provider
      value={{
        visibleSection,
        setVisibleSection,
        otherApplicantData,
        ownApplicantData,
        handleApplicantDataChange,
        setOwnApplicantData,
        otherPartnerData,
        ownPartnerData,
        handlePartnerDataChange,
        setOwnPartnerData,
        otherAddressData,
        otherHouseData,
        ownAddressData,
        ownHouseData,
        handleAddressDataChange,
        handleHouseDataChange,
        setOwnAddressData,
        setOwnHouseData,
        otherMortgageData,
        ownMortgageData,
        handleMortgageDataChange,
        setOwnMortgageData,
        ownInsuranceData,
        handleInsuranceDataChange,
        setOwnInsuranceData,
        otherInsuranceData,
        meta,
        showOnlyDiff: visibleSection === null,
        hasDiff,
        from: 'Gegevens van je adviseur',
        contactInfoFields,
        healthFields,
        personFields,
        workIncomeFields,
        homeFields,
        addressFields,
        mortgageFields,
        loanpartFields,
        orvFields,
        orvCoverageFields,
        shouldShow,
        copiedFields,
        setCopiedFields,
        isFieldCopied,
        hasPageCopiedFields,
        handleCopyAllFields,
        hasSectionCopiedFields,
        customOwnAddressSetter,
      }}
    >
      <PreventUnload>
        {isLoading && <Loader />}
        <Stack
          as="form"
          onSubmit={onSave}
          space="xl"
          width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
        >
          <Header
            title={t('Gegevens ophalen van je adviseur')}
            icon={<ImportSvg height="2rem" width="2rem" />}
            variant="page"
            button={<Button type="submit">{t('Opslaan')}</Button>}
          />
          <Card>
            {t(
              'In dit scherm zie je links de gegevens zoals je ze nog hebt en rechts de gegevens volgens opgave van de partij waarmee je de gegevens vergelijkt. Je kunt deze gegevens per regel simpel overnemen met het blauwe pijltje. Tevens is er een optie om alle gegevens over te nemen per categorie zodra je tevreden bent.'
            )}
          </Card>
          <CompareApplicant />
          {!(isEmpty(ownPartnerData) && isEmpty(otherPartnerData)) && (
            <ComparePartner />
          )}
          <CompareResidence />
          <CompareMortgage />
          <CompareOrv />
          <Flex justifyContent="flex-end">
            <Button variant="primary" onClick={handleCopyDossier}>
              {t('Volledig dossier overnemen')}
            </Button>
          </Flex>
        </Stack>
      </PreventUnload>
    </CompareContext.Provider>
  );
};

ComparePage.propTypes = {
  veiligOphalen: bool,
};

ComparePage.defaultProps = {
  veiligOphalen: false,
};

export default ComparePage;
